import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TextField } from '@mui/material'
import { useField } from 'formik'
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'
import { isNil } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import Loader from '../loader/Loader'
import { AutoCompleteFieldProps, autocompletePopupIndicatorSlotProps, listOption } from './AutoCompleteField'
import { DEFAULT_DEBOUNCE_TIME } from '../../utils/defaultDebounceTime'

interface AsyncAutoCompleteFieldProps extends AutoCompleteFieldProps {
  getValue: (value: string) => void
  debounceTime?: number
  loading?: boolean
}

const minWidth = { minWidth: '30rem' }

const AsyncAutoCompleteField: React.FC<AsyncAutoCompleteFieldProps> = ({
  name,
  label,
  options = [],
  getValue,
  required = false,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  loading: loadingFromProps = false,
  ...props
}) => {
  const [field, , { setValue }] = useField({ name })
  const [optionsList, setOptionsList] = useState<listOption[]>([])
  const [initialListIsReady, setInitialListIsReady] = useState(false)
  const [listLoading, setListLoading] = useState(true)

  const onChange = useCallback((_, nextSelectedOption) => setValue(nextSelectedOption?.value), [setValue])

  const getOptionLabel = useCallback(
    (option) => option?.label || options.find((o) => option === o.value)?.label || '',
    [options]
  )

  const getOptionKey = useCallback((option: listOption) => String(option?.value ?? option?.label ?? ''), [])

  useEffect(() => {
    setOptionsList(options)
    setListLoading(false)
  }, [options])

  const optionsWithDefaultEmptyValue = useMemo(() => [{ value: '' }, ...optionsList], [optionsList])

  const debounced = useDebouncedCallback((value) => getValue(value), debounceTime)

  const updateSearchValueOnlyOnTyping = (value: string, reason: AutocompleteInputChangeReason): void => {
    if (reason === 'input') {
      setListLoading(true)
      setOptionsList([])
      debounced(value)
    }
  }

  useEffect(() => {
    if (optionsList.length > 0 && !initialListIsReady) setInitialListIsReady(true)
  }, [optionsList])

  if (!initialListIsReady) return <Loader />
  return (
    <Autocomplete
      {...props}
      sx={minWidth}
      value={field.value || null}
      options={optionsWithDefaultEmptyValue} // add empty value for initial value...
      filterOptions={(options) => options.filter((option) => option.value !== '')} // ...then filter it
      onInputChange={(_, value, reason) => updateSearchValueOnlyOnTyping(value, reason)}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      isOptionEqualToValue={(option, value) => !isNil(option) && option.value === value}
      onChange={onChange}
      loading={listLoading || loadingFromProps}
      slotProps={autocompletePopupIndicatorSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          variant="outlined"
          size="small"
          margin="normal"
          fullWidth
        />
      )}
    />
  )
}

export default AsyncAutoCompleteField
